import { Fragment, useEffect, useState } from 'react';

import { t } from 'i18next';

import { COLUMNS_MARKET, useMarketsContext } from './MarketContext';

import { Button, Loading } from '../UI';
import { Column } from '../NewStrategy/StrategyTable';
import { MarketListRow } from './MarketListRow';
import { Sort } from '../NewStrategy/Sort';

import { useSort } from '../../hooks/useSort';
import { sortAssets } from './helpers';
import { MarketColumn } from './types';
import { Debug } from '../Debug/Debug';
import ExchangeFilter from './ExchangeFilter';

export const columnClassName = 'flex items-center';

export const MarketList = () => {
  const {
    assetsList,
    assetType,
    assetExchange,
    loadingExtractAssets,
    noActiveFilter,
    setAssetExchange,
    loadMore,
    tokens,
  } = useMarketsContext();

  const { direction, type, getSortValue, handleSort } = useSort(
    [...COLUMNS_MARKET.CRYPTO],
    ''
  );

  useEffect(() => {
    handleSort('', direction);
  }, [assetType]);

  const [isButtonLoading, setIsButtonLoading] = useState(false);

  const columns = COLUMNS_MARKET[assetType];

  const assets = type
    ? [...assetsList].sort((a, b) =>
        sortAssets(a, b, type as MarketColumn, getSortValue)
      )
    : assetsList;

  const gridColClassName = `
    inline-grid
    grid-cols-${columns.length}
    p-2
    ml-4
    w-full
    gap-x-2
  `;

  const handleLoadMore = () => {
    setIsButtonLoading(true);
    loadMore().finally(() => setIsButtonLoading(false));
  };

  return (
    <>
      <Debug>
        <>
          {`${assetType} ${assetExchange}`}
          {assetsList?.length}
        </>
      </Debug>

      <>
        <div
          className={
            'font-semibold text-left text-sm my-5 mx-3 uppercase flex justify-between'
          }
        >
          <div>{assetType}</div>

          <div className={'w-32 mr-4'}>
            {assetType === 'STOCK' && (
              <ExchangeFilter
                width={100}
                defaultCountry={'United States'}
                onFiltersChange={(e) => setAssetExchange(e.join(','))}
              />
            )}
          </div>
        </div>
      </>

      <div
        className={`
          shadow-md
          border
          border-opacity-50
          border-grey-30
          my-2
          mx-2
          rounded-md
          relative
          dark:border-grey-80
          dark:divide-grey-80
        `}
      >
        <div className={'divide-grey-10 divide-y-1 overflow-auto text-left'}>
          <div className={`flex w-full h-full`}>
            <div
              className={`
                  flex
                  w-16
                  h-auto
                  items-center
                  justify-center
                  text-xs
                  font-medium
                  pl-3
                  uppercase
                `}
            >
              {t('common.rank')}
            </div>
            <div
              className={`
                  inline-grid
                  grid-cols-${columns.length}
                  p-2
                  w-full
                  uppercase
                  mx-4
                `}
            >
              {columns.map((columnName, i) => (
                <Column
                  key={columnName}
                  // className={}
                  element={
                    <Sort
                      className={`
                          ${columnClassName}
                          text-xs
                          font-medium
                        `}
                      columnName={columnName}
                      type={type}
                      handleSort={handleSort}
                    />
                  }
                  span={1}
                />
              ))}
            </div>
          </div>
          <div className={'overflow-auto'} style={{ height: '70vh' }}>
            {loadingExtractAssets ? (
              <Loading
                status={true}
                size={'large'}
                className={'m-auto h-full'}
              />
            ) : assets.length ? (
              assets.map((asset, i) => (
                <Fragment key={asset.id}>
                  <MarketListRow
                    asset={asset}
                    gridColClassName={gridColClassName}
                    key={asset.id}
                    rank={i + 1}
                  />
                  <Debug>
                    <>{`
                    Exchange: ${asset?.exchange || ''}
                    ProviderID: ${asset?.providerId}
                    ID: ${asset?.id} 
                    MarketCap: ${asset?.marketCap}
                     `}</>
                  </Debug>
                </Fragment>
              ))
            ) : (
              // FIXME temporary component, does not exist on Figma
              <div className="flex h-full">
                <span className="m-auto rounded-lg border border-grey-30 p-28 bg-grey-5 dark:bg-grey-70">
                  {t('markets.noDataAfterFilter')}
                </span>
              </div>
            )}

            {noActiveFilter && assets.length > 0 && (
              <div className="flex justify-center mb-5 mt-5">
                <Button
                  type="button"
                  variant={'primary'}
                  disabled={!tokens[assetType]}
                  onClick={handleLoadMore}
                  loading={isButtonLoading}
                >
                  <div className="capitalize-first">
                    {`${t('markets.loadMore')}...`}
                  </div>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
